import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormMessage,
} from "@/components/ui/form";
import { subscriptionSchema, type SubscriptionFormValues } from "@/schemas/subscriptionSchema";
import { useFormSubmission } from "@/hooks/use-form-submission";
import { formService } from "@/services/formService";
import AnimatedBackground from "./AnimatedBackground";
import Particles from "./Particles";

import OptiCrmImg from "@/assets/Optibizcrm.png";
import OptiPosImg from "@/assets/Asset 10.png";
import OptiHrmImg from "@/assets/HRM-Icon.png";
import OptiEduImg from "@/assets/optibizedu.png";

const Hero = () => {
  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      email: "",
    },
  });

  const { isLoading, handleSubmit: submitForm } = useFormSubmission({
    submitFn: (data: SubscriptionFormValues) => formService.submitDemoRequest(data),
    successMessage: "Demo Request Received! We'll contact you shortly.",
    onSuccess: () => form.reset(),
  });

  const onSubmit = (data: SubscriptionFormValues) => {
    submitForm(data);
  };
  return (
    <section className="relative pb-32 -mt-2 w-full text-center overflow-hidden">
      {/* Advanced animated background */}
      <AnimatedBackground variant="mesh" className="absolute inset-0 -z-10" />

      {/* Particles effect */}
      <Particles count={40} className="opacity-60 dark:opacity-30" />

      {/* Animated dotted lines SVG with glow */}
      <div className="absolute inset-0 pointer-events-none hidden md:block">
        <svg className="w-full h-full" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Top Left to Center */}
          <path d="M200 150 Q 400 150 600 300" stroke="currentColor" className="text-optibiz-blue/40 dark:text-optibiz-blue/60 animate-pulse" strokeWidth="2" strokeDasharray="6 6" filter="url(#glow)" />
          {/* Top Right to Center */}
          <path d="M1000 200 Q 800 200 600 300" stroke="currentColor" className="text-optibiz-green/40 dark:text-optibiz-green/60 animate-pulse" strokeWidth="2" strokeDasharray="6 6" style={{ animationDelay: '0.3s' }} filter="url(#glow)" />
          {/* Bottom Left to Center */}
          <path d="M250 600 Q 400 600 600 450" stroke="currentColor" className="text-optibiz-orange/40 dark:text-optibiz-orange/60 animate-pulse" strokeWidth="2" strokeDasharray="6 6" style={{ animationDelay: '0.6s' }} filter="url(#glow)" />
          {/* Bottom Right to Center */}
          <path d="M950 550 Q 800 550 600 450" stroke="currentColor" className="text-optibiz-yellow/40 dark:text-optibiz-yellow/60 animate-pulse" strokeWidth="2" strokeDasharray="6 6" style={{ animationDelay: '0.9s' }} filter="url(#glow)" />
        </svg>
      </div>

      {/* Floating Avatars (Decorative) with enhanced animations */}
      <div className="absolute top-[15%] left-[5%] md:left-[10%] animate-bounce-soft z-10 hidden md:block">
        <div className="relative">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-red-500 dark:border-red-600 shadow-2xl overflow-hidden ring-4 ring-white/20 dark:ring-white/10 backdrop-blur-sm">
            <img src={OptiCrmImg} alt="CRM" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -right-12 -top-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg text-optibiz-blue dark:text-optibiz-blue text-xs font-bold flex items-center gap-1 border border-optibiz-blue/20 dark:border-optibiz-blue/40 animate-slide-in-right">
            <span className="w-2 h-2 bg-optibiz-blue rounded-full animate-pulse"></span> CRM
          </div>
        </div>
      </div>

      <div className="absolute top-[20%] right-[5%] md:right-[12%] animate-bounce-soft z-10 hidden md:block" style={{ animationDelay: '0.2s' }}>
        <div className="relative">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-green-500 dark:border-green-600 shadow-2xl overflow-hidden ring-4 ring-white/20 dark:ring-white/10 backdrop-blur-sm">
            <img src={OptiPosImg} alt="POS" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -left-10 -bottom-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-full text-optibiz-orange dark:text-optibiz-orange shadow-lg text-xs font-bold flex items-center gap-1 animate-slide-in-left">
            <span className="w-2 h-2 bg-optibiz-orange rounded-full animate-pulse"></span> POS
          </div>
        </div>
      </div>

      <div className="absolute bottom-[5%] md:bottom-[20%] left-[5%] md:left-[15%] animate-bounce-soft z-10 hidden md:block" style={{ animationDelay: '0.4s' }}>
        <div className="relative">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-red-500 dark:border-red-600 shadow-2xl overflow-hidden ring-4 ring-white/20 dark:ring-white/10 backdrop-blur-sm">
            <img src={OptiHrmImg} alt="RentToday" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -right-10 -top-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-full text-optibiz-green dark:text-optibiz-green shadow-lg text-xs font-bold flex items-center gap-1 animate-slide-in-right">
            <span className="w-2 h-2 bg-optibiz-green rounded-full animate-pulse"></span> RentToday
          </div>
        </div>
      </div>

      <div className="absolute bottom-[32%] md:bottom-[25%] right-[5%] md:right-[8%] animate-bounce-soft z-10 hidden md:block" style={{ animationDelay: '0.6s' }}>
        <div className="relative">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-green-500 dark:border-green-600 shadow-2xl overflow-hidden ring-4 ring-white/20 dark:ring-white/10 backdrop-blur-sm">
            <img src={OptiEduImg} alt="EDU" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -left-10 -top-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-full text-optibiz-yellow dark:text-optibiz-yellow shadow-lg text-xs font-bold flex items-center gap-1 animate-slide-in-left">
            <span className="w-2 h-2 bg-optibiz-yellow rounded-full animate-pulse"></span> EDU
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto relative z-20 mt-10 px-4">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-tasktide-dark dark:text-white leading-[1.05] mb-6 tracking-tight animate-slide-in-down">
          Transform Your Business with <br />
          OptiBiz Suite
        </h1>

        <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-600 dark:text-gray-300 mb-6 animate-slide-in-up" style={{ animationDelay: '0.1s' }}>
          Empowering African Businesses to Thrive
        </h2>

        <p className="text-gray-500 dark:text-gray-400 text-sm md:text-xl mb-12 max-w-2xl mx-auto font-light leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
          Comprehensive business management software tailored for African enterprises, SMEs, and NGOs. Streamline operations, boost productivity, and drive growth with our integrated suite of applications.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 max-w-lg mx-auto bg-white/40 dark:bg-gray-800/40 p-2 rounded-lg md:rounded-full shadow-2xl dark:shadow-2xl border border-optibiz-blue/20 dark:border-optibiz-blue/30 backdrop-blur-xl hover:border-optibiz-blue/40 dark:hover:border-optibiz-blue/50 transition-all duration-300 animate-slide-in-up" style={{ animationDelay: '0.3s' }}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col sm:flex-row w-full gap-2 flex-1">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <input
                        {...field}
                        type="email"
                        placeholder="Enter your email address"
                        className="flex-1 px-6 py-3 outline-none text-gray-700 dark:text-gray-200 bg-transparent w-full placeholder:text-gray-400 dark:placeholder:text-gray-500 font-medium"
                      />
                    </FormControl>
                    <FormMessage className="absolute -bottom-6 left-6 text-xs text-red-500" />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-gradient-to-r from-optibiz-blue to-optibiz-green dark:from-optibiz-orange dark:to-optibiz-yellow text-white hover:from-optibiz-blue/90 hover:to-optibiz-green/90 dark:hover:from-optibiz-orange/90 dark:hover:to-optibiz-yellow/90 rounded-lg md:rounded-full px-8 py-6 w-full sm:w-auto text-base font-bold transition-all relative overflow-hidden group shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative z-10 flex items-center">
                  {isLoading ? "Requesting..." : <>Request a Demo <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-optibiz-yellow/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default Hero;
